import React from 'react';
import Stack from '@mui/material/Stack';
import MenuItem from '@mui/material/MenuItem';
import { Control, FieldValues, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  CheckoutFormProps,
  DefaultFormActions,
  Form,
  useEnhancedForm,
} from '../../apiForm/form';
import { TextField, SelectField } from '../../apiForm/form/input';
import ArrayWrapper from '../../apiForm/wrapper/ArrayWrapper';
import CheckboxField from '../../apiForm/form/input/CheckboxField';
import ExpandableFieldset from '../../apiForm/form/ExpandableFieldset';
import { CustomerValidationTypes } from '../../project/CheckoutConfig';

function CustomerValidationSelect({
  name,
  control,
  disabled,
}: {
  control: Control;
  disabled: boolean | undefined;
  name: string;
}) {
  const { t } = useTranslation();

  return (
    <SelectField
      name={name}
      control={control}
      disabled={disabled}
      label={t('checkoutConfig.label.customerValidations.type')}
    >
      {Object.values(CustomerValidationTypes).map((type) => (
        <MenuItem key={type} value={type}>
          {type
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (char) => char.toUpperCase())}
        </MenuItem>
      ))}
    </SelectField>
  );
}

function getHelperTextFor(type: string): string {
  if (type === CustomerValidationTypes.LOYALTY_CARD) {
    return 'checkoutConfig.helperText.customerValidations.loyaltyCard';
  }

  if (type === CustomerValidationTypes.NATIONALITY) {
    return 'checkoutConfig.helperText.customerValidations.nationality';
  }

  return '';
}

function MethodSpecificFields({
  control,
  namePrefix,
  watch,
  readonly,
}: {
  namePrefix: string;
  watch: UseFormWatch<FieldValues>;
  control: Control;
  readonly: boolean | undefined;
}) {
  const { t } = useTranslation();
  const type = watch(`${namePrefix}.type`);

  return (
    <>
      {(type === CustomerValidationTypes.LOYALTY_CARD ||
        type === CustomerValidationTypes.NATIONALITY) && (
        <TextField
          control={control}
          name={`${namePrefix}.validationExpression`}
          optional
          disabled={readonly}
          label={t(
            'checkoutConfig.label.customerValidations.validationExpression',
          )}
          helperText={t(getHelperTextFor(type))}
        />
      )}

      {type === CustomerValidationTypes.LOYALTY_CARD && (
        <TextField
          control={control}
          name={`${namePrefix}.scannableTemplate`}
          optional
          disabled={readonly}
          label={t(
            'checkoutConfig.label.customerValidations.scannableTemplate',
          )}
          helperText={t(
            'checkoutConfig.helperText.customerValidations.scannableTemplate',
          )}
        />
      )}

      {type === CustomerValidationTypes.LOYALTY_CARD && (
        <CheckboxField
          control={control}
          name={`${namePrefix}.required`}
          disabled={readonly}
          label={t('checkoutConfig.label.customerValidations.required')}
        />
      )}
    </>
  );
}

export default function CustomerValidationForm({
  defaultValues = {},
  errors = {},
  onSubmit,
  readonly,
}: CheckoutFormProps<any>) {
  const { t } = useTranslation();

  const { handleSubmit, control, formState, getValues, reset, watch } =
    useEnhancedForm({ defaultValues, errors });

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <ExpandableFieldset
        legend={t('checkoutConfig.legend.customerValidations')}
      >
        <ArrayWrapper
          control={control}
          name="customerValidations"
          defaultValue={{}}
          addLabel={t('checkoutConfig.arrayWrapper.customerValidation.add')}
          removeLabel={t(
            'checkoutConfig.arrayWrapper.customerValidation.remove',
          )}
          render={(index: number) => (
            <Stack spacing={1}>
              <CustomerValidationSelect
                control={control}
                name={`customerValidations.[${index}].type`}
                disabled={readonly}
              />

              <MethodSpecificFields
                control={control}
                readonly={readonly}
                namePrefix={`customerValidations.[${index}]`}
                watch={watch}
              />
            </Stack>
          )}
        />
      </ExpandableFieldset>

      <DefaultFormActions
        formState={formState}
        cancelNavigationTarget="/"
        noCancelNavigation
        reset={reset}
        getValues={getValues}
      />
    </Form>
  );
}
